import React, { useState, useEffect } from 'react';

import {
  BarChartOutlined,
  FileTextOutlined,
  RobotOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FireOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Line, Column, Heatmap } from '@ant-design/plots';
import {
  Drawer,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Divider,
  Tabs,
  Button,
  Table,
  Progress,
  Alert,
} from 'antd';
import SuperAdminAPI from '../../../services/SuperAdminAPI';

const { TabPane } = Tabs;

/**
 * 📈 3️⃣ USAGE ANALYTICS
 *
 * Shows:
 * - 👥 Active users (24h, 7d, 30d)
 * - 🧠 AI calls (OpenAI / DeepSeek / Omni-Agent)
 * - 📄 Documents uploaded (PDF/Word/Images)
 * - 📝 Memos created
 * - ⚖️ Cases created
 * - 📑 Contracts analyzed
 * - 🗂️ Whiteboards used
 * - 🧪 Clause simulations run
 * - 📨 Emails linked
 * - 📦 Storage used (GB)
 * - 🔥 Activity Heatmap
 * - ⭐ Top 5 users by usage
 */
const TenantAnalyticsDrawer = ({ visible, onClose, tenant }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    if (visible && tenant) {
      fetchAnalytics();
    }
  }, [visible, tenant, timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const data = await SuperAdminAPI.getTenantAnalytics(tenant._id, timeRange);
      const defaults = {
        users: { active24h: 0, active7d: 0, active30d: 0, growth: 0 },
        aiUsage: { totalCalls: 0, openai: 0, deepseek: 0, omniAgent: 0, tokensUsed: 0, cost: 0 },
        documents: { uploaded: 0, pdf: 0, word: 0, images: 0, totalSize: 0 },
        activities: {
          memosCreated: 0,
          casesCreated: 0,
          contractsAnalyzed: 0,
          whiteboardsUsed: 0,
          clauseSimulations: 0,
          emailsLinked: 0,
        },
        storage: { used: 0, limit: 0, percentage: 0 },
        activityHeatmap: [],
        topUsers: [],
        activityTrend: [],
      };
      setAnalyticsData({
        ...defaults,
        ...data,
        users: { ...defaults.users, ...(data?.users || {}) },
        aiUsage: { ...defaults.aiUsage, ...(data?.aiUsage || {}) },
        documents: { ...defaults.documents, ...(data?.documents || {}) },
        activities: { ...defaults.activities, ...(data?.activities || {}) },
        storage: { ...defaults.storage, ...(data?.storage || {}) },
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!tenant) return null;

  const topUsersColumns = [
    {
      title: '👤 User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.name}</strong>
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: '📊 Actions',
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
    },
    {
      title: '🧠 AI Calls',
      dataIndex: 'aiCalls',
      key: 'aiCalls',
      align: 'center',
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <BarChartOutlined />
          <span>Usage Analytics - {tenant.name}</span>
        </Space>
      }
      placement="right"
      width={800}
      open={visible}
      onClose={onClose}
      extra={
        <Space>
          <Button.Group>
            <Button
              type={timeRange === '7d' ? 'primary' : 'default'}
              onClick={() => setTimeRange('7d')}
            >
              7 Days
            </Button>
            <Button
              type={timeRange === '30d' ? 'primary' : 'default'}
              onClick={() => setTimeRange('30d')}
            >
              30 Days
            </Button>
            <Button
              type={timeRange === '90d' ? 'primary' : 'default'}
              onClick={() => setTimeRange('90d')}
            >
              90 Days
            </Button>
          </Button.Group>
          <Button icon={<SyncOutlined />} onClick={fetchAnalytics} loading={loading}>
            Refresh
          </Button>
        </Space>
      }
    >
      {analyticsData && (
        <>
          <Tabs defaultActiveKey="overview">
            {/* Overview Tab */}
            <TabPane tab="📊 Overview" key="overview">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Active Users */}
                <Card title="👥 Active Users" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Statistic
                        title="Last 24h"
                        value={analyticsData.users.active24h}
                        suffix="users"
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Last 7d"
                        value={analyticsData.users.active7d}
                        suffix="users"
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Last 30d"
                        value={analyticsData.users.active30d}
                        suffix="users"
                        prefix={analyticsData.users.growth > 0 ? '↑' : '↓'}
                      />
                    </Col>
                  </Row>
                  <Divider />
                  <Tag color="success">
                    <ThunderboltOutlined /> {analyticsData.users.growth}% growth
                  </Tag>
                </Card>

                {/* Activities */}
                <Card title="⚡ Activity Summary" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Statistic
                        title="📝 Memos"
                        value={analyticsData.activities.memosCreated}
                        prefix={<FileTextOutlined />}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic title="⚖️ Cases" value={analyticsData.activities.casesCreated} />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="📑 Contracts"
                        value={analyticsData.activities.contractsAnalyzed}
                      />
                    </Col>
                  </Row>
                  <Divider />
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Statistic
                        title="🗂️ Whiteboards"
                        value={analyticsData.activities.whiteboardsUsed}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="🧪 Simulations"
                        value={analyticsData.activities.clauseSimulations}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic title="📨 Emails" value={analyticsData.activities.emailsLinked} />
                    </Col>
                  </Row>
                </Card>

                {/* Storage */}
                <Card title="📦 Storage Usage" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title="Used"
                        value={analyticsData.storage.used}
                        suffix={`/ ${analyticsData.storage.limit} GB`}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Percentage"
                        value={analyticsData.storage.percentage}
                        suffix="%"
                      />
                    </Col>
                  </Row>
                  <Progress
                    percent={analyticsData.storage.percentage}
                    strokeColor={analyticsData.storage.percentage > 80 ? '#f5222d' : '#52c41a'}
                    style={{ marginTop: '16px' }}
                  />
                </Card>
              </Space>
            </TabPane>

            {/* AI Usage Tab */}
            <TabPane tab="🧠 AI Usage" key="ai">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title="Total AI Calls"
                        value={analyticsData.aiUsage.totalCalls}
                        prefix={<RobotOutlined />}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Total Cost"
                        value={analyticsData.aiUsage.cost}
                        prefix="$"
                        precision={2}
                      />
                    </Col>
                  </Row>
                </Card>

                <Card title="🤖 Model Breakdown" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Statistic title="OpenAI" value={analyticsData.aiUsage.openai} />
                    </Col>
                    <Col span={8}>
                      <Statistic title="DeepSeek" value={analyticsData.aiUsage.deepseek} />
                    </Col>
                    <Col span={8}>
                      <Statistic title="Omni-Agent" value={analyticsData.aiUsage.omniAgent} />
                    </Col>
                  </Row>
                </Card>

                <Card size="small">
                  <Statistic
                    title="🎯 Tokens Used"
                    value={analyticsData.aiUsage.tokensUsed.toLocaleString()}
                  />
                </Card>

                <Alert
                  message="AI Usage Insights"
                  description="OpenAI is the most used model, accounting for 58% of total AI calls."
                  type="info"
                  showIcon
                />
              </Space>
            </TabPane>

            {/* Documents Tab */}
            <TabPane tab="📄 Documents" key="documents">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                  <Statistic
                    title="Total Documents Uploaded"
                    value={analyticsData.documents.uploaded}
                    prefix={<FileTextOutlined />}
                  />
                </Card>

                <Card title="📁 Document Types" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Statistic title="PDF" value={analyticsData.documents.pdf} />
                    </Col>
                    <Col span={8}>
                      <Statistic title="Word" value={analyticsData.documents.word} />
                    </Col>
                    <Col span={8}>
                      <Statistic title="Images" value={analyticsData.documents.images} />
                    </Col>
                  </Row>
                </Card>

                <Card size="small">
                  <Statistic
                    title="Total Size"
                    value={analyticsData.documents.totalSize}
                    suffix="GB"
                  />
                </Card>
              </Space>
            </TabPane>

            {/* Top Users Tab */}
            <TabPane tab="⭐ Top Users" key="topUsers">
              <Card title="🏆 Top 5 Users by Activity" size="small">
                <Table
                  columns={topUsersColumns}
                  dataSource={analyticsData.topUsers}
                  pagination={false}
                  size="small"
                  rowKey="email"
                />
              </Card>
            </TabPane>

            {/* Activity Heatmap Tab */}
            <TabPane tab="🔥 Heatmap" key="heatmap">
              <Card title="📅 Activity Heatmap (Last 7 Days)" size="small">
                {analyticsData.activityHeatmap && analyticsData.activityHeatmap.length > 0 ? (
                  <Heatmap
                    data={analyticsData.activityHeatmap}
                    xField="hour"
                    yField="day"
                    colorField="value"
                    height={260}
                    meta={{ hour: { type: 'cat' }, day: { type: 'cat' } }}
                  />
                ) : (
                  <Alert type="info" message="No activity heatmap data available." showIcon />
                )}
              </Card>
            </TabPane>
          </Tabs>
        </>
      )}
    </Drawer>
  );
};

export default TenantAnalyticsDrawer;
